import React, { useCallback } from 'react';
import { Route, BrowserRouter as Router } from 'react-router-dom';
import { Button, Flexbox, Icon, Navbar } from '@hiiretail/synergy-ui';
import { NotificationsProvider } from './common/notifications/Notifications';
import { useIntl } from 'react-intl';

import {
  AccessControl,
  Breadcrumbs,
  CommonMessages,
  LoggedInRouteGuard,
  UserMenu,
  useTokenInterceptor,
} from './common';
import {
  CustomRolesList,
  GroupDetails,
  GroupList,
  Home,
  IdentityProviders,
  Permissions,
  Profile,
  Tenants,
  UserDetails,
  UserSearch,
} from './pages';
import { AppWrapper, NavbarWrapper } from './App.styles';
import { BuiltInRoles, BuiltInRoleDetails } from './pages/BuiltInRoles';
import { BreadcrumbsActionsProvider } from './common/components/Breadcrumbs/context/BreadcrumbsActionsContext';
import { CustomRoleDetails } from './pages/CustomRoles/CustomRoleDetails';
import { MyTenant } from './pages/MyTenant';
import { useGetTenantAlias } from './common/hooks/useGetTenantAlias/useGetTenantAlias';
import { NoPermissionsHandlerScreen } from './pages/NoPermissions/NoPermissionsHandlerScreen';
import { ExternalApps } from './pages/ExternalApps';
import { DevLogin } from 'pages/DevLogin';
import { useAuth } from '@hiiretail/core';

const { PUBLIC_URL } = process.env;

type MenuItem = {
  title: string;
  route: string;
  extendaSpecific: boolean;
  permission: string;
};

const App: React.FC = () => {
  const intl = useIntl();
  const { logoutWithIdp, userInfo, isAuthenticated } = useAuth();
  const tenantAlias = useGetTenantAlias();

  useTokenInterceptor().then();

  const menuItems: MenuItem[] = [
    {
      title: intl.formatMessage(CommonMessages.tenants),
      route: '/tenants',
      extendaSpecific: true,
      permission: 'iam.tenant.list',
    },
    {
      title: intl.formatMessage(CommonMessages.users),
      route: '/users',
      extendaSpecific: false,
      permission: 'iam.user.find',
    },
    {
      title: intl.formatMessage(CommonMessages.groupList),
      route: '/groups',
      extendaSpecific: false,
      permission: 'iam.group.list',
    },
    {
      title: intl.formatMessage(CommonMessages.permissions),
      route: '/permissions',
      extendaSpecific: true,
      permission: 'iam.permission.list',
    },
    {
      title: intl.formatMessage(CommonMessages.identityProviders),
      route: '/identity-providers',
      extendaSpecific: false,
      permission: 'iam.provider.list',
    },
    {
      title: 'External Apps',
      route: '/external-app',
      extendaSpecific: false,
      permission: 'iam.external-app.list',
    },
  ];

  const shouldShowMenuItem = (menuItem: MenuItem) => {
    return (
      !(menuItem.extendaSpecific && tenantAlias !== 'extenda') ||
      process.env.NODE_ENV === 'development'
    );
  };

  const logout = useCallback(async () => {
    const redirectUrl = `${window.location.origin}/login`;
    const tenantId = localStorage.getItem('tenantId');

    if (tenantId) {
      await logoutWithIdp(tenantId, redirectUrl);
    }
  }, [logoutWithIdp]);

  if (!isAuthenticated && process.env.NODE_ENV === 'development') {
    return <DevLogin />;
  }

  return (
    <Router basename={PUBLIC_URL}>
      <AppWrapper container direction="column">
        <BreadcrumbsActionsProvider>
          <NavbarWrapper container item>
            <Navbar.Container>
              <Navbar.Nav>
                <Navbar.NavItem logo to="/">
                  <Icon.ExtendaLogo size="lg" color={() => 'white'} />
                </Navbar.NavItem>
                {menuItems.map((menuItem) =>
                  shouldShowMenuItem(menuItem) ? (
                    <AccessControl
                      renderIfNoAccess={null}
                      permission={menuItem.permission}
                      key={`${menuItem.route} ${menuItem.title}`}
                    >
                      <Navbar.NavItem to={menuItem.route}>{menuItem.title}</Navbar.NavItem>
                    </AccessControl>
                  ) : null,
                )}
                <AccessControl renderIfNoAccess={null} permission={'iam.role.list'}>
                  <Navbar.Dropdown title="Roles">
                    <Navbar.DropdownItem to="/built-in-roles">
                      {intl.formatMessage(CommonMessages.builtInRoles)}
                    </Navbar.DropdownItem>
                    <Navbar.DropdownItem to="/custom-roles">Custom Roles</Navbar.DropdownItem>
                  </Navbar.Dropdown>
                </AccessControl>
              </Navbar.Nav>
              <UserMenu title={userInfo?.name || ''} onLogoutBtnClick={() => logout()}>
                <Flexbox item>
                  <Button fullWidth to="/my-tenant" variant="secondary">
                    My tenant
                  </Button>
                </Flexbox>
              </UserMenu>
            </Navbar.Container>
          </NavbarWrapper>
          <Breadcrumbs />
          <NoPermissionsHandlerScreen>
            <NotificationsProvider>
              <LoggedInRouteGuard>
                <Route exact path="/" component={Home} />
                <Route exact path="/tenants" component={Tenants} />
                <Route exact path="/users" component={UserSearch} />
                <Route exact path="/users/:id" component={UserDetails} />
                <Route exact path="/permissions" component={Permissions} />
                <Route exact path="/groups" component={GroupList} />
                <Route exact path="/groups/:id" component={GroupDetails} />
                <Route exact path="/identity-providers" component={IdentityProviders} />
                {/*HII-3727: Adds backwards compatibility with routes from old IAM UI */}
                <Route exact path="/providers" component={IdentityProviders} />
                <Route exact path="/roles" component={BuiltInRoles} />
                <Route exact path="/roles/:id" component={BuiltInRoleDetails} />
                {/*HII-3727: Adds backwards compatibility with routes from old IAM UI */}
                <Route exact path="/built-in-roles" component={BuiltInRoles} />
                <Route exact path="/built-in-roles/:id" component={BuiltInRoleDetails} />
                <Route exact path="/custom-roles" component={CustomRolesList} />
                <Route exact path="/custom-roles/:id" component={CustomRoleDetails} />
                <Route exact path="/profile" component={Profile} />
                <Route exact path="/my-tenant" component={MyTenant} />
                <Route exact path="/external-app" component={ExternalApps} />
              </LoggedInRouteGuard>
            </NotificationsProvider>
          </NoPermissionsHandlerScreen>
        </BreadcrumbsActionsProvider>
      </AppWrapper>
    </Router>
  );
};

export default App;
